import { api } from '@/api/api.v2';
import { proxy } from 'valtio';
import storeRematch from '@/store';
import type { CreatedRapper, Rapper, UpdatedRapper, RootState } from '@/components/WellLoggingOperations/types';

import type {
    WellsRappersStore,
    WellsRappersResponse,
    WellsRappersPayload,
    RappersByWells,
} from '../types/wellsRappers';
import { arrayEquals } from '../@lib';

/*
    TODO: стоит ли кэшировать наборы rapper-ов для скважины?
    Ответить на вопрос после того, когда будет сделано ОБНОВЛЕНИЕ rapper-ов.
*/

/*
    TODO: возможно стоит разделить модель.
    Разделить на:
    - работа с загрузкой rappers
    - работа с rappers после загрузки
*/

export const store: WellsRappersStore = proxy({
    /*
        wells - это наборы реперов, привязанных к конкретным скважинам.
    */
    rappersByWells: null,

    isLoading: false,
    isError: false,

    /*
        Вспомогательное поле - значение для сохранения параметров запроса.
    */
    prevWellIdsParam: [],
    /*
        Вспомогательное поле - значение для сохранения предыдущей активной версии.
    */
    prevActiveVersionIdParam: null,
});

/*
    ====== Private Methods ======
*/

const setResponseData = (response: WellsRappersResponse) => {
    const { packs } = response;

    store.rappersByWells = packs;
    store.isLoading = false;
};

const startRequest = () => {
    store.isLoading = true;
    store.isError = false;
};

const setError = () => {
    store.isLoading = false;
    store.isError = true;
};

const setPreviousParams = (wellIds: number[], activeVersionId: number | null) => {
    store.prevWellIdsParam = wellIds;
    store.prevActiveVersionIdParam = activeVersionId;
};

/*
    Проверка на равенство предыдущих и текущих параметров для запроса.
    При успешной проверке, запроса не происходит.
*/
const isEqualParams = (wellIds: number[], activeVersionId: number | null) => {
    const { prevWellIdsParam, prevActiveVersionIdParam } = store;

    const isEqualParams = arrayEquals(prevWellIdsParam, wellIds);
    const isEqualActiveID = prevActiveVersionIdParam === activeVersionId;

    return isEqualParams && isEqualActiveID;
};

/*
    Запрос за получением rapper-ов по скважинам
*/
const fetchWellsRappers = async (payload: WellsRappersPayload) => {
    const body = JSON.stringify(payload);
    return await api.carottage.fetchVersionWellPacks(body);
};

/*
    ====== Public Methods ======
*/

/*
    Полная перезапись состояния
*/
export const setWellsRappers = (wellsRappers: RappersByWells | null) => {
    store.rappersByWells = wellsRappers;
};

/*
    Добавляет по wellId любой (оригинальный, новый) rapper в список.
*/
export const addRappersToWell = (wellId: number, rapper: Rapper | CreatedRapper) => {
    const { rappersByWells } = store;

    if (rappersByWells) {
        const rappers = rappersByWells[wellId] ?? [];
        rappersByWells[wellId] = [...rappers, rapper];
    }
};

/*
    Обновляет ОДИН rapper у скважины по его index-у
*/
export const updateRapperByIdx = (wellId: number, idx: number, rapper: CreatedRapper | UpdatedRapper) => {
    const { rappersByWells } = store;

    if (rappersByWells && wellId in rappersByWells) {
        const rappers = rappersByWells[wellId] ?? [];
        rappers[idx] = rapper;
    }
};

/*
    Обновляет ВЕСЬ список rappers у скважины
*/
export const updateRapperByWellId = (wellId: number, rappers: Array<Rapper | CreatedRapper | UpdatedRapper>) => {
    const { rappersByWells } = store;

    if (rappersByWells && wellId in rappersByWells) {
        rappersByWells[wellId] = rappers;
    }
};

/*
    Событие для запроса.
    Формирует параметры, согласно документации.
    Сами параметры собирается из redux состояния.
*/
export const getWellsRappers = async (force = false) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    const { tablets, rappers } = storeRematch.getState() as RootState;

    const { isGroup, activeTabletId, activeGroupIds } = tablets;
    const { activeVersionID } = rappers;

    if (!activeVersionID) return;

    let wellIds = [];

    if (activeTabletId) wellIds.push(activeTabletId);
    if (isGroup) wellIds = wellIds.concat(activeGroupIds);

    /*
        Повторный запрос происходит при условии, что предыдущие параметры НЕ совпадают с текущими и
        ID активной версии так же различаются
    */
    const isEqual = isEqualParams(wellIds, activeVersionID);
    if (isEqual && !force) return;

    const payload = {
        wellId: wellIds,
        versionId: activeVersionID,
    };

    setPreviousParams(wellIds, activeVersionID);
    startRequest();

    try {
        const response = await fetchWellsRappers(payload);
        setResponseData(response);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log('error:getting wells rappers', error);
        setError();
    }
};

/*
    Получить rappers для скважины, если не нужна реактивность в компоненте.
*/
export const getRappersByWell = (wellId: number): Array<Rapper | CreatedRapper | UpdatedRapper> => {
    const { rappersByWells } = store;

    if (rappersByWells && wellId in rappersByWells) {
        return rappersByWells[wellId] ?? [];
    }

    return [];
};

/*
    Удаляет все rappers, которые есть в store
    Метод будет вызван в rematch модели.
*/
export const removeAllRappers = () => {
    setWellsRappers(null);
};

/*
    Удаляет все rappers, которые привязаны к версии rapper.
    Метод будет вызван в rematch модели.
*/
export const removeRappersByPackId = (packID: number) => {
    const { rappersByWells } = store;

    if (!rappersByWells) return;

    const wellIds = Object.keys(rappersByWells);

    const result: RappersByWells = {};

    for (const wellId of wellIds) {
        if (wellId in rappersByWells) {
            const rappers = getRappersByWell(Number(wellId));
            const filteredRappers = rappers.filter(x => x.packID !== packID);

            result[wellId] = filteredRappers;
        }
    }

    setWellsRappers(result);
};
